import { Viewer, Worker } from "@react-pdf-viewer/core";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Navigate } from "react-router-dom";
import tickGreen from "../../assets/images/gif/geentick.gif";
import returnGif from "../../assets/images/gif/return.gif";
import ActionDialog from "../../components/CommonComponents/DialogConfirmation/ActionDialog";
import { DocumentPreviewDialog } from "../../components/CommonComponents/DialogConfirmation/DocPreviewDialog";
import { ErrorToast } from "../../components/toastMessage";
import { isLevel2 } from "../../hoc/isLevel2";
import {
  useUpdateUserVerificationStatusForLevelTwo,
  useUserVerificationComments,
} from "../../hooks/level-2-user-verification.hooks";
import { getDocumentTypesByUserType } from "./LevelTwo.constants";

function LevelTwoUserVerification() {
  const navigate = useNavigate();
  const { state: userDetails } = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [action, setAction] = useState<{ type: "return" | "approved" | null }>({
    type: null,
  });
  const [document, setDocument] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const mutation = useUpdateUserVerificationStatusForLevelTwo();
  const { data } = useUserVerificationComments({
    userId: userDetails?.userId ? +userDetails?.userId : undefined,
  });

  const commentList = data?.data || [];

  if (!userDetails?.userId) {
    return <Navigate to="/level-2-user-verifications-list" />;
  }

  const toggleModal = (document: string) => {
    setDocument(document);
  };

  const updateUserVerificationDetails = (isReturn: boolean) => {
    if (!comment) {
      ErrorToast("Please enter a valid comment!");
      return;
    }
    mutation.mutate(
      {
        action: isReturn ? "reject" : "approve",
        comment,
        email: userDetails.email,
        name: userDetails.name,
        userId: userDetails?.userId,
      },
      {
        onSuccess: () => {
          setAction({ type: isReturn ? "return" : "approved" });
          if (!isReturn) {
            setDialogOpen(true);
          }
          setTimeout(() => {
            navigate("/level-2-user-verifications-list");
          }, 5000);
        },
      }
    );
  };

  const isImageUrl = (url: string | null) => {
    return /\.(jpg|jpeg|png|svg)$/.test(url ?? "");
  };

  const docsToRender = getDocumentTypesByUserType(
    userDetails?.user_type,
    userDetails?.document_type
  );

  return (
    <>
      <div className="m-5 bg-white rounded-xl shadow-lg border border-gray-300 p-4 md:p-5 space-y-6">
        <h3 className="text-center my-3 text-xl font-bold">
          Application Details
        </h3>

        <div className="grid grid-cols-2 gap-5">
          <div className="flex justify-between items-center col-span-2 md:col-span-1 gap-2">
            <p className="font-bold basis-1/3">Name</p>
            <p className="text-muted basis-2/3 ">{userDetails.name || "--"}</p>
          </div>
          <div className="flex justify-between items-center col-span-2 md:col-span-1 gap-2">
            <p className="font-bold basis-1/3">Email</p>
            <p className="text-muted basis-2/3 ">{userDetails.email || "--"}</p>
          </div>
          <div className="flex justify-between items-center col-span-2 md:col-span-1 gap-2">
            <p className="font-bold basis-1/3">Mobile Number</p>
            <p className="text-muted basis-2/3 ">
              {userDetails.mobile_number || "--"}
            </p>
          </div>

          <div className="flex justify-between items-center col-span-2 md:col-span-1 gap-2">
            <p className="font-bold basis-1/3">Aadhaar/Other Govt.ID</p>
            <p className="text-muted basis-2/3 ">
              {userDetails.gov_id_number || "--"}
            </p>
          </div>

          <div className="flex justify-between items-center col-span-2 md:col-span-1 gap-2">
            <p className="font-bold basis-1/3">Office Address</p>
            <p className="text-muted basis-2/3">
              {userDetails.office_address || "--"}
            </p>
          </div>
        </div>

        {/* <div className="flex flex-wrap md:flex-nowrap gap-4 md:justify-between"> */}
        <div className="py-5 flex flex-wrap gap-10">
          {docsToRender
            ?.filter((e) => !!userDetails[e.key])
            .map((e) => (
              <div
                className="flex justify-between items-center w-full md:w-1/2"
                key={e.key}
              >
                <p className="font-bold ">{e.label}</p>
                <button
                  onClick={() => toggleModal(userDetails?.[e.key])}
                  className="px-4 py-1.5 border-amber-400 border rounded-md items-center justify-around"
                >
                  View
                  <i className="ml-2 mr-0 fa fa-eye text-primary"></i>
                </button>
              </div>
            ))}
        </div>
        {!!commentList?.length && (
          <div>
            {commentList?.map((item) => (
              <div key={item.id}>
                <p className="font-bold justify-around mb-1">
                  {item.comments_by ?? ""} {" Reviewer Remark"}
                </p>
                <p className=" justify-around">{item?.comment ?? ""}</p>
              </div>
            ))}
          </div>
        )}

        {!userDetails?.regestration_status && (
          <div>
            <p className="font-bold justify-around my-4">
              Level 2 Reviewer Remark <span className="text-red-500">*</span>
            </p>
            <div className="flex gap-4 flex-wrap md:flex-nowrap items-center">
              <textarea
                value={comment}
                className="basis-full md:basis-4/5"
                placeholder="Add Comment"
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="flex gap-4 justify-evenly md:flex-col  basis-full md:basis-1/5">
                <button
                  disabled={
                    comment.length === 0 ||
                    mutation.isPending ||
                    mutation.isSuccess
                  }
                  onClick={() => updateUserVerificationDetails(true)}
                  className={`text-base font-bold text-white py-1.5 rounded-md md:block md:w-full px-4 basis-1/2 ${
                    comment.length === 0 ? "bg-gray-400" : "bg-red-400"
                  }`}
                >
                  Return
                </button>
                <button
                  disabled={
                    comment.length === 0 ||
                    mutation.isPending ||
                    mutation.isSuccess
                  }
                  onClick={() => updateUserVerificationDetails(false)}
                  className={`text-base font-bold text-white py-1.5 rounded-md md:block md:w-full px-4 basis-1/2 ${
                    comment.length === 0 ? "bg-gray-400" : "bg-green-600"
                  }`}
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-center items-center">
          <button
            type="button"
            onClick={() => navigate("/level-2-user-verifications-list")}
            className="bg-black px-6 py-2 rounded-full font-bold text-white"
          >
            Close
          </button>
        </div>
      </div>
      {!!document && (
        <DocumentPreviewDialog
          onClose={() => setDocument(null)}
          open={!!document}
        >
          {document.endsWith(".pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={document} />
            </Worker>
          ) : (
            <img src={document} alt="Document" />
          )}
        </DocumentPreviewDialog>
      )}

      <ActionDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        actionType="approved"
        imageSrc={tickGreen}
        title="Registration Request Forwarded"
        description="Registration request is forwarded to level 3 officer."
        navigateTo="/level-2-user-verifications-list"
      />

      <ActionDialog
        isOpen={action.type === "return"}
        onClose={() => {}}
        actionType="return"
        imageSrc={returnGif}
        title="Application Returned"
        description="User application returned. Status sent to user."
        navigateTo="/level-2-user-verifications-list"
      />
    </>
  );
}

export default isLevel2(LevelTwoUserVerification);
